// The canvas: pointer, wheel and key handling, and drawing. A port of AnnotationCanvas
// (frontend/ui/annotation_canvas.py) with the select/pan, box, polygon and rotated-box tools.
import { AnnotationDoc, getGeom, setGeom, setObb, translated, type Ann, type Geom } from "./doc";
import {
  CURSOR_FOR_HANDLE, handleAt, handleRects, normalized, polygonBounds, rectsIntersect, resizeRect, boxShapeContains, sceneLength,
  type Handle, type Pt, type Rect,
} from "./geometry";
import {
  CLOSE_SNAP_DISTANCE, MIN_VERTICES, ROTATE_HANDLE_GAP, VERTEX_RADIUS, angleAbout, centroid, cornersOf, dragVertex, edgeAt, obbHandleAt, obbHandleRects,
  obbResize, obbRotateCenter, obbRotateRect, obbRotateTo, obbShapeContains, onPolyGrip, polyGripRect, polyHandleAt, polyHandleRects, polygonShapeContains,
  rotateDrag, type Obb, type ObbHandle,
} from "./shapes";
import { Viewport, WHEEL_STEP } from "./viewport";
import { decodeWheel } from "./wheel";

type Drag =
  | { kind: "pan"; x: number; y: number }
  | { kind: "box"; start: Pt; cur: Pt }
  | { kind: "move"; start: Pt; origs: { ann: Ann; old: Geom }[]; moved: boolean; narrow: Ann | null }
  | { kind: "resize"; ann: Ann; handle: Handle; old: Rect }
  | { kind: "vertex"; ann: Ann; index: number; old: Pt[] }
  | { kind: "protate"; ann: Ann; pivot: Pt; startAngle: number; old: Pt[] }
  | { kind: "oresize"; ann: Ann; handle: ObbHandle; old: Obb }
  | { kind: "orotate"; ann: Ann; old: Obb }
  | { kind: "obox"; start: Pt; cur: Pt }
  | { kind: "band"; start: Pt; cur: Pt; base: Set<number> };

/** What a click on a selected shape's handle grabs. */
type HandleHit =
  | { t: "bbox"; handle: Handle }
  | { t: "vertex"; index: number }
  | { t: "protate" }
  | { t: "oresize"; handle: ObbHandle }
  | { t: "orotate" };

export interface MenuEntry { label: string; hint?: string; disabled?: boolean; separator?: boolean; run?: () => void }
export interface ContextMenuRequest { x: number; y: number; items: MenuEntry[] }

/** Qt's double-click window and distance. */
const DOUBLE_MS = 500, DOUBLE_PX = 5;
const POLY_TOOL_COLOR = "#81C784";

const isTextField = (el: Element | null) => !!el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || (el as HTMLElement).isContentEditable);

export class CanvasController {
  readonly vp = new Viewport();
  bitmap: ImageBitmap | null = null;
  private drag: Drag | null = null;
  private spacePan = false;
  private raf = 0;
  private needFit = false;
  private dpr = 1;
  private hoverCursor = "";
  private unsub: (() => void) | null = null;
  /** The polygon being traced (PolygonTool._points) and where the rubber band ends. */
  private poly: Pt[] = [];
  private polyCursor: Pt | null = null;
  private lastDown: { t: number; x: number; y: number; b: number } | null = null;
  private suppressMenu = false;
  /** The page shows a menu for a right-click on a polygon. */
  onContextMenu: ((m: ContextMenuRequest) => void) | null = null;

  constructor(private doc: AnnotationDoc, private root: HTMLElement, private base: HTMLCanvasElement, private over: HTMLCanvasElement) {}

  // ── Lifecycle ────────────────────────────────────────────────────────────────────────────────
  attach(): () => void {
    const { root, over } = this;
    const on = <K extends keyof HTMLElementEventMap>(el: HTMLElement, t: K, f: (e: HTMLElementEventMap[K]) => void, o?: AddEventListenerOptions) => {
      el.addEventListener(t, f as EventListener, o);
      return () => el.removeEventListener(t, f as EventListener, o);
    };
    const offs = [
      on(over, "pointerdown", this.onDown), on(over, "pointermove", this.onMove), on(over, "pointerup", this.onUp),
      on(over, "pointercancel", this.onUp), on(over, "contextmenu", this.onContext),
      on(over, "pointerenter", this.takeKeyboard), on(root, "wheel", this.onWheel, { passive: false }),
      on(root, "keydown", this.onKeyDown), on(root, "keyup", this.onKeyUp), on(root, "blur", this.onBlur),
    ];
    const ro = new ResizeObserver(() => this.resize());
    ro.observe(root);
    this.unsub = this.doc.subscribe(() => { this.cancelDragIfModeChanged(); this.schedule(); });
    this.resize();
    return () => { offs.forEach((f) => f()); ro.disconnect(); this.unsub?.(); cancelAnimationFrame(this.raf); };
  }

  setImage(bitmap: ImageBitmap | null) {
    this.bitmap = bitmap;
    this.drag = null;
    this.poly = []; this.polyCursor = null;
    if (bitmap) { this.vp.setImage(bitmap.width, bitmap.height); this.needFit = true; this.applyPendingFit(); }
    this.schedule();
  }

  /** Depth view: the same sensor grid under another skin, so the view, shapes and selection stay exactly as they are. */
  swapBitmap(bitmap: ImageBitmap) {
    const same = this.bitmap && this.bitmap.width === bitmap.width && this.bitmap.height === bitmap.height;
    this.bitmap = bitmap;
    if (!same) { this.vp.setImage(bitmap.width, bitmap.height); this.needFit = true; this.applyPendingFit(); }
    this.schedule();
  }

  private resize() {
    const r = this.root.getBoundingClientRect();
    this.dpr = window.devicePixelRatio || 1;
    for (const c of [this.base, this.over]) {
      c.width = Math.max(1, Math.round(r.width * this.dpr));
      c.height = Math.max(1, Math.round(r.height * this.dpr));
      c.style.width = `${r.width}px`;
      c.style.height = `${r.height}px`;
    }
    this.vp.setSize(r.width, r.height);
    this.applyPendingFit();
    this.schedule();
  }

  private applyPendingFit() {
    if (this.needFit && this.vp.w > 0 && this.bitmap) { this.vp.fit(); this.needFit = false; }
  }

  // ── Public view controls (rail buttons and keys) ─────────────────────────────────────────────
  /** Rail buttons: 1.2 per click. */
  zoomButton(dir: "in" | "out") { this.vp.zoomBy(dir === "in" ? 1.2 : 1 / 1.2); this.schedule(); }
  /** `+` and `-`: two wheel notches about the view centre. */
  zoomKey(dir: "in" | "out") { this.vp.zoomBy(dir === "in" ? WHEEL_STEP ** 2 : 1 / WHEEL_STEP ** 2); this.schedule(); }
  /** fit_to_view: the image together with everything drawn on it. */
  fit() {
    let b: Rect = { x1: 0, y1: 0, x2: this.vp.iw, y2: this.vp.ih };
    for (const a of this.doc.anns) {
      const r = a.rect ?? (a.points ? polygonBounds(a.points) : null);
      if (r) b = { x1: Math.min(b.x1, r.x1), y1: Math.min(b.y1, r.y1), x2: Math.max(b.x2, r.x2), y2: Math.max(b.y2, r.y2) };
    }
    this.vp.fit(b);
    this.schedule();
  }
  /** AnnotationCanvas.escape: discard a shape being drawn. True when a polygon in progress was thrown away (the tool stays). */
  escape(): boolean {
    const m = this.doc.mode;
    if (m === "polygon" && this.poly.length) {
      const had = this.poly.length;
      this.clearPoly();
      this.doc.say(`Polygon cancelled — ${had} point${had !== 1 ? "s" : ""} discarded.`);
      this.schedule();
      return true;
    }
    if ((m === "obb" && this.drag?.kind === "obox") || (m === "bbox" && this.drag?.kind === "box")) { this.drag = null; this.schedule(); }
    return false;
  }

  /** Leaving a tool abandons its half-made shape (set_mode). */
  private cancelDragIfModeChanged() {
    const m = this.doc.mode;
    if (this.drag?.kind === "box" && m !== "bbox") this.drag = null;
    if (this.drag?.kind === "obox" && m !== "obb") this.drag = null;
    if (m !== "polygon" && this.poly.length) this.clearPoly();
  }

  // ── Polygon tool (PolygonTool) ───────────────────────────────────────────────────────────────
  isDrawing(): boolean { return this.poly.length > 0; }
  private clearPoly() { this.poly = []; this.polyCursor = null; }

  private announcePoly() {
    const n = this.poly.length;
    if (!n) return;
    this.doc.say(n < 3
      ? `Polygon: ${n} point${n !== 1 ? "s" : ""} — at least 3 needed. Esc cancels.`
      : `Polygon: ${n} points — press Enter or double-click to close, Backspace removes the last, Esc cancels.`);
  }

  private polygonPress(scene: Pt, double: boolean) {
    if (double) { this.closePolygon(); return; }
    // Clicking the first point again closes the shape; the radius is screen pixels, so it stays clickable at any zoom.
    if (this.poly.length >= 3) {
      const f = this.poly[0];
      if (Math.hypot(scene[0] - f[0], scene[1] - f[1]) <= sceneLength(CLOSE_SNAP_DISTANCE, this.vp.scale)) { this.closePolygon(); return; }
    }
    this.poly.push(scene);
    this.announcePoly();
    this.schedule();
  }

  /** Enter, right-click, double-click or the first point: fewer than three points is discarded, out loud. */
  closePolygon() {
    const pts = this.poly;
    this.clearPoly();
    this.schedule();
    if (pts.length < MIN_VERTICES) {
      if (pts.length) this.doc.say(`Polygon discarded — ${pts.length} point${pts.length !== 1 ? "s" : ""} does not enclose an area (3 or more needed).`);
      return;
    }
    this.doc.addPolygon(pts);
    this.doc.say(`Polygon closed — ${pts.length} points. Select it to drag a corner, double-click an edge to add one, Alt+click a corner to remove it.`);
  }

  /** Backspace while drawing: take back the last point. */
  undoPoint() {
    if (!this.poly.length) return;
    this.poly.pop();
    this.polyCursor = null;
    this.announcePoly();
    this.schedule();
  }

  // ── Hit testing ──────────────────────────────────────────────────────────────────────────────
  /** The topmost shape under a scene point (a shape added later draws, and hits, on top). Selected shapes include their handles. */
  topHit(p: Pt): Ann | null {
    const s = this.vp.scale;
    for (let i = this.doc.anns.length - 1; i >= 0; i--) {
      const a = this.doc.anns[i];
      const sel = this.doc.selection.has(a.key);
      const inside = a.kind === "bbox" ? boxShapeContains(a.rect!, p, s, sel) : a.obb ? obbShapeContains(a.obb, p, s, sel) : polygonShapeContains(a.points!, p, s, sel);
      if (inside) return a;
    }
    return null;
  }

  /** The handle of a selected shape under p (BBoxItem / PolygonItem / OBBItem `_handle_at`). */
  private handleOf(a: Ann, p: Pt): HandleHit | null {
    const s = this.vp.scale;
    if (a.kind === "bbox") { const h = handleAt(a.rect!, p, s, true); return h ? { t: "bbox", handle: h } : null; }
    if (a.obb) {
      const h = obbHandleAt(a.obb, p, s, true);
      return h === "rotate" ? { t: "orotate" } : h ? { t: "oresize", handle: h } : null;
    }
    if (onPolyGrip(a.points!, p, s, true)) return { t: "protate" };
    const i = polyHandleAt(a.points!, p, s, true);
    return i === null ? null : { t: "vertex", index: i };
  }

  private handleHit(p: Pt): { ann: Ann; hit: HandleHit } | null {
    const a = this.topHit(p);
    if (!a || !this.doc.selection.has(a.key)) return null;
    const hit = this.handleOf(a, p);
    return hit ? { ann: a, hit } : null;
  }

  // ── Pointer ──────────────────────────────────────────────────────────────────────────────────
  private local(e: PointerEvent | MouseEvent): [number, number] {
    const r = this.over.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  }

  private takeKeyboard = () => { if (!isTextField(document.activeElement)) this.root.focus({ preventScroll: true }); };

  /** Qt turns the second press of a quick pair into a double-click event; there is no native one for pointer events. */
  private isDouble(e: PointerEvent, x: number, y: number): boolean {
    const l = this.lastDown;
    const dbl = !!l && l.b === e.button && e.timeStamp - l.t <= DOUBLE_MS && Math.abs(x - l.x) <= DOUBLE_PX && Math.abs(y - l.y) <= DOUBLE_PX;
    this.lastDown = dbl ? null : { t: e.timeStamp, x, y, b: e.button };
    return dbl;
  }

  private onDown = (e: PointerEvent) => {
    e.preventDefault();                                                    // no text selection or native drag from the canvas
    this.takeKeyboard();
    const [sx, sy] = this.local(e);
    const scene = this.vp.toScene(sx, sy);
    const left = e.button === 0;
    const dbl = this.isDouble(e, sx, sy);
    if (e.button === 2) {                                                  // right-click closes a polygon being drawn; otherwise it is the polygon menu (contextmenu event)
      if (this.doc.mode === "polygon" && this.poly.length) { this.closePolygon(); this.suppressMenu = true; }
      return;
    }
    if (!left && e.button !== 1) return;
    this.over.setPointerCapture(e.pointerId);
    const d = this.doc;

    if ((this.spacePan && left) || e.button === 1) { this.drag = { kind: "pan", x: sx, y: sy }; this.setCursor("grabbing"); return; }
    if (d.frozen) return;                                                  // an image is loading, or the account is read-only: nothing to edit
    if (dbl && this.doubleClick(scene)) return;
    if (d.mode === "bbox") { this.drag = { kind: "box", start: scene, cur: scene }; this.schedule(); return; }

    const hit = this.topHit(scene);
    if (d.mode === "polygon") {
      // Mid-trace every click is a point, even one inside a finished polygon; otherwise a press on a shape reaches the shape.
      if (this.poly.length || !hit) { this.polygonPress(scene, dbl); return; }
      this.itemPress(e, scene, sx, sy, hit);
      return;
    }
    if (d.mode === "obb") {
      if (hit) this.itemPress(e, scene, sx, sy, hit);
      else { this.drag = { kind: "obox", start: scene, cur: scene }; this.schedule(); }
      return;
    }

    // Select / pan. A selected shape's handle takes the press whatever modifiers are held (Shift snaps a rotation to 15°).
    const grabbed = this.handleHit(scene);
    if (grabbed) { this.beginHandle(e, scene, grabbed.ann, grabbed.hit); return; }
    if (e.shiftKey || e.ctrlKey || e.metaKey) {                            // multi-select: toggle one shape, or rubber-band
      if (hit) d.toggle(hit.key);
      else this.drag = { kind: "band", start: scene, cur: scene, base: new Set(d.selection) };
      return;
    }
    if (!hit) { d.clearSelection(); this.drag = { kind: "pan", x: sx, y: sy }; this.setCursor("grabbing"); return; }
    this.itemPress(e, scene, sx, sy, hit);
  };

  /** A press that lands on a shape (QGraphicsItem.mousePressEvent plus each item's own handle and move logic). */
  private itemPress(e: PointerEvent, scene: Pt, sx: number, sy: number, hit: Ann) {
    const d = this.doc;
    const ctrl = e.ctrlKey || e.metaKey;
    if (!d.selection.has(hit.key)) {
      if (d.mode === "select") {                                           // first click selects (and pans); a second drag moves
        d.select([hit.key]);
        this.drag = { kind: "pan", x: sx, y: sy };
        this.setCursor("grabbing");
        return;
      }
      if (ctrl) d.toggle(hit.key); else d.select([hit.key]);               // in the other tools the same press also starts moving it
      this.beginMove(scene, null);
      return;
    }
    const h = this.handleHit(scene);
    if (h) { this.beginHandle(e, scene, h.ann, h.hit); return; }
    this.beginMove(scene, ctrl ? null : hit);
  }

  private beginMove(scene: Pt, narrow: Ann | null) {
    this.drag = { kind: "move", start: scene, origs: this.doc.selected().map((ann) => ({ ann, old: getGeom(ann) })), moved: false, narrow };
  }

  private beginHandle(e: PointerEvent, scene: Pt, ann: Ann, h: HandleHit) {
    switch (h.t) {
      case "bbox": this.drag = { kind: "resize", ann, handle: h.handle, old: { ...ann.rect! } }; break;
      case "vertex":
        if (e.altKey) { this.removeVertexReporting(ann, h.index); break; }   // Alt+click a corner drops it
        this.drag = { kind: "vertex", ann, index: h.index, old: ann.points!.map((p) => [...p] as Pt) };
        break;
      case "protate": {
        const old = ann.points!.map((p) => [...p] as Pt), pivot = centroid(old);
        this.drag = { kind: "protate", ann, pivot, startAngle: angleAbout(pivot, scene), old };
        break;
      }
      case "oresize": this.drag = { kind: "oresize", ann, handle: h.handle, old: { ...ann.obb! } }; break;
      case "orotate": this.drag = { kind: "orotate", ann, old: { ...ann.obb! } }; break;
    }
  }

  /** Double-click: closes the polygon being drawn, or puts a corner on the polygon edge under the pointer. */
  private doubleClick(scene: Pt): boolean {
    if (this.doc.mode === "polygon" && this.poly.length) { this.closePolygon(); return true; }
    const hit = this.topHit(scene);
    if (!hit || hit.kind !== "polygon" || hit.obb) return false;
    return this.insertVertexReporting(hit, scene);
  }

  private insertVertexReporting(a: Ann, scene: Pt): boolean {
    const edge = edgeAt(a.points!, scene, this.vp.scale);
    if (!edge) return false;
    this.doc.selection = new Set([...this.doc.selection, a.key]);
    this.doc.insertVertex(a, edge.index, edge.point);
    this.doc.say(`Point added — ${a.points!.length} corners. Drag the new handle to place it.`);
    return true;
  }

  private removeVertexReporting(a: Ann, index: number) {
    if (this.doc.removeVertex(a, index)) this.doc.say(`Point removed — ${a.points!.length} corners left. Ctrl+Z puts it back.`);
    else this.doc.say(`A polygon needs at least ${MIN_VERTICES} corners — delete the whole annotation instead.`);
  }

  private onMove = (e: PointerEvent) => {
    const [sx, sy] = this.local(e);
    const scene = this.vp.toScene(sx, sy);
    const dr = this.drag;
    if (!dr) {
      if (this.doc.mode === "polygon" && this.poly.length) { this.polyCursor = scene; this.schedule(); }   // the rubber band
      this.updateHoverCursor(scene);
      return;
    }
    switch (dr.kind) {
      case "pan": this.vp.panBy(sx - dr.x, sy - dr.y); dr.x = sx; dr.y = sy; break;
      case "box": case "obox": dr.cur = scene; break;
      case "move": {
        const dx = scene[0] - dr.start[0], dy = scene[1] - dr.start[1];
        for (const o of dr.origs) setGeom(o.ann, translated(o.old, dx, dy));
        dr.moved = dx !== 0 || dy !== 0;
        break;
      }
      case "resize": dr.ann.rect = resizeRect(dr.old, dr.handle, scene); break;
      case "vertex": dr.ann.points = dragVertex(dr.old, dr.index, scene, this.vp.iw, this.vp.ih); break;
      case "protate": dr.ann.points = rotateDrag(dr.old, dr.pivot, dr.startAngle, scene, e.shiftKey); break;
      case "oresize": setObb(dr.ann, obbResize(dr.old, dr.handle, scene)); break;
      case "orotate": setObb(dr.ann, obbRotateTo(dr.old, scene, e.shiftKey)); break;
      case "band": {
        dr.cur = scene;
        const band = normalized({ x1: dr.start[0], y1: dr.start[1], x2: scene[0], y2: scene[1] });
        const inside = this.doc.anns.filter((a) => rectsIntersect(a.rect ?? polygonBounds(a.points!), band)).map((a) => a.key);
        this.doc.select([...dr.base, ...inside]);
        break;
      }
    }
    this.schedule();
  };

  private onUp = (e: PointerEvent) => {
    if (e.button === 2) return;                                            // the right button never ends a left drag
    const dr = this.drag;
    this.drag = null;
    if (this.over.hasPointerCapture?.(e.pointerId)) this.over.releasePointerCapture(e.pointerId);
    if (!dr) return;
    const d = this.doc;
    switch (dr.kind) {
      case "box": d.addBox({ x1: dr.start[0], y1: dr.start[1], x2: dr.cur[0], y2: dr.cur[1] }); break;
      case "obox": {
        const a = d.addObb({ x1: dr.start[0], y1: dr.start[1], x2: dr.cur[0], y2: dr.cur[1] });
        d.say(a ? "Box drawn — drag the round handle to rotate, or [ and ] to nudge" : "Box discarded — drag out a rectangle rather than clicking");
        break;
      }
      case "move":
        if (dr.moved) d.commitMoves(dr.origs.map((o) => ({ ann: o.ann, old: o.old, next: getGeom(o.ann) })));
        else if (dr.narrow && d.selection.size > 1) d.select([dr.narrow.key]);   // a click that did not drag leaves just that shape selected
        break;
      case "resize": d.commitResize(dr.ann, dr.old, dr.ann.rect!); break;
      case "vertex": case "protate":
        if (JSON.stringify(dr.old) !== JSON.stringify(dr.ann.points)) d.commitReshape(dr.ann, dr.old, dr.ann.points!);
        break;
      case "oresize": case "orotate": d.commitTransform(dr.ann, dr.old, dr.ann.obb!); break;
      case "pan": case "band": break;
    }
    this.hoverCursor = "";
    this.updateHoverCursor(this.vp.toScene(...this.local(e)));
    this.schedule();
  };

  /** Right-click on a polygon: the same two edits, named (PolygonItem.contextMenuEvent). */
  private onContext = (e: MouseEvent) => {
    e.preventDefault();
    if (this.suppressMenu) { this.suppressMenu = false; return; }          // that right-click closed a polygon
    if (this.doc.mode === "polygon" && this.poly.length) return;
    const scene = this.vp.toScene(...this.local(e));
    const a = this.topHit(scene);
    if (!a || a.kind !== "polygon" || a.obb || !this.onContextMenu) return;
    this.doc.selection = new Set([...this.doc.selection, a.key]);
    this.doc.touch();
    const idx = polyHandleAt(a.points!, scene, this.vp.scale, true);
    const edge = edgeAt(a.points!, scene, this.vp.scale);
    const items: MenuEntry[] = [];
    if (edge && idx === null) items.push({ label: "Insert point here", run: () => this.insertVertexReporting(a, scene) });
    if (idx !== null) items.push({ label: "Delete this point", hint: "Alt+click", disabled: a.points!.length <= MIN_VERTICES, run: () => this.removeVertexReporting(a, idx) });
    if (items.length) items.push({ label: "", separator: true });
    items.push({ label: "Delete annotation", run: () => this.doc.deleteOne(a) });
    this.onContextMenu({ x: e.clientX, y: e.clientY, items });
  };

  private onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const r = this.over.getBoundingClientRect();
    const a = decodeWheel(e);
    if (a.kind === "zoom") this.vp.zoomBy(a.factor, [e.clientX - r.left, e.clientY - r.top]);
    else if (a.kind === "pan") this.vp.panBy(a.dx, a.dy);
    this.schedule();
  };

  // ── Keys the canvas itself owns (everything else goes through the shell's shortcut table) ────
  private onKeyDown = (e: KeyboardEvent) => {
    if (e.key === " " && !isTextField(e.target as Element)) {
      e.preventDefault();
      if (!e.repeat) { this.spacePan = true; this.setCursor("grab"); }
    }
  };
  private onKeyUp = (e: KeyboardEvent) => {
    if (e.key === " ") { this.spacePan = false; if (this.drag?.kind !== "pan") this.setCursor(this.modeCursor()); }
  };
  private onBlur = () => { this.spacePan = false; };

  // ── Cursor ───────────────────────────────────────────────────────────────────────────────────
  private modeCursor() { return this.doc.mode === "select" ? "grab" : this.doc.mode === "bbox" ? "crosshair" : "default"; }
  private setCursor(c: string) { if (this.hoverCursor !== c) { this.hoverCursor = c; this.over.style.cursor = c; } }
  private updateHoverCursor(scene: Pt) {
    if (this.spacePan) return this.setCursor("grab");
    const h = this.handleHit(scene);                                       // a selected shape's handles, in any tool
    if (h) {
      return this.setCursor(h.hit.t === "bbox" ? CURSOR_FOR_HANDLE[h.hit.handle] : h.hit.t === "protate" || h.hit.t === "orotate" ? "crosshair" : "move");
    }
    this.setCursor(this.modeCursor());
  }

  // ── Drawing ──────────────────────────────────────────────────────────────────────────────────
  schedule() { if (!this.raf) this.raf = requestAnimationFrame(() => { this.raf = 0; this.render(); }); }

  render() {
    this.root.dataset.scale = String(this.vp.scale);
    this.root.dataset.tx = String(this.vp.tx);
    this.root.dataset.ty = String(this.vp.ty);
    this.root.dataset.dirty = String(this.doc.dirty);
    this.drawBase();
    this.drawOverlay();
  }

  private drawBase() {
    const ctx = this.base.getContext("2d")!;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.base.width, this.base.height);
    if (!this.bitmap) return;
    ctx.setTransform(this.dpr * this.vp.scale, 0, 0, this.dpr * this.vp.scale, this.dpr * this.vp.tx, this.dpr * this.vp.ty);
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(this.bitmap, 0, 0);
  }

  private drawOverlay() {
    const ctx = this.over.getContext("2d")!;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.vp.w, this.vp.h);
    const s = this.vp.scale;
    ctx.lineJoin = "bevel";                                  // Qt's default pen join
    const lineW = Math.max(2 * s, 1);                       // a 2-unit pen in scene space (as Qt draws it), never thinner than a pixel
    const sx = (x: number) => this.vp.tx + x * s, sy = (y: number) => this.vp.ty + y * s;
    const handleSquare = (r: Rect) => {
      const x = sx(r.x1), y = sy(r.y1), w = (r.x2 - r.x1) * s;
      ctx.fillRect(x, y, w, w); ctx.strokeRect(x, y, w, w);
    };
    const ring = (pts: Pt[]) => { ctx.beginPath(); pts.forEach(([x, y], i) => (i ? ctx.lineTo(sx(x), sy(y)) : ctx.moveTo(sx(x), sy(y)))); ctx.closePath(); };
    const circle = (c: Pt, r: number) => { ctx.beginPath(); ctx.arc(sx(c[0]), sy(c[1]), r * s, 0, Math.PI * 2); };

    const drawBox = (r: Rect, color: string, selected: boolean) => {
      ctx.fillStyle = hexA(color, 40 / 255);
      ctx.fillRect(sx(r.x1), sy(r.y1), (r.x2 - r.x1) * s, (r.y2 - r.y1) * s);
      ctx.strokeStyle = color; ctx.lineWidth = lineW; ctx.setLineDash([]);
      ctx.strokeRect(sx(r.x1), sy(r.y1), (r.x2 - r.x1) * s, (r.y2 - r.y1) * s);
      if (selected) {
        ctx.lineWidth = 1; ctx.strokeStyle = "#222222"; ctx.fillStyle = "#FFFFFF";
        for (const h of Object.values(handleRects(r, s))) handleSquare(h);
      }
    };

    const drawPolygon = (pts: Pt[], color: string, selected: boolean) => {
      ring(pts);
      ctx.fillStyle = hexA(color, 40 / 255); ctx.fill();
      ctx.strokeStyle = color; ctx.lineWidth = lineW; ctx.setLineDash([]); ctx.stroke();
      if (!selected) return;
      ctx.lineWidth = 1; ctx.strokeStyle = "#222222"; ctx.fillStyle = "#FFFFFF";
      for (const h of polyHandleRects(pts, s)) handleSquare(h);
      const grip = polyGripRect(pts, s), gc: Pt = [(grip.x1 + grip.x2) / 2, (grip.y1 + grip.y2) / 2], b = polygonBounds(pts);
      ctx.strokeStyle = color; ctx.beginPath(); ctx.moveTo(sx((b.x1 + b.x2) / 2), sy(b.y1)); ctx.lineTo(sx(gc[0]), sy(grip.y2)); ctx.stroke();
      ctx.strokeStyle = "#222222"; ctx.fillStyle = color; circle(gc, (grip.x2 - grip.x1) / 2); ctx.fill(); ctx.stroke();
    };

    const drawObb = (o: Obb, color: string, selected: boolean) => {
      const c = cornersOf(o);
      ring(c);
      ctx.fillStyle = hexA(color, 40 / 255); ctx.fill();
      ctx.strokeStyle = color; ctx.lineWidth = lineW; ctx.setLineDash([]); ctx.stroke();
      if (!selected) return;
      this.selectionOutline(ctx, c, s, sx, sy);
      ctx.lineWidth = 1; ctx.strokeStyle = "#222222"; ctx.fillStyle = "#FFFFFF";
      for (const h of Object.values(obbHandleRects(o, s))) handleSquare(h);
      const g = obbRotateCenter(o, s);
      ctx.strokeStyle = "#FFFFFF"; ctx.beginPath();                         // a stalk to the grip
      ctx.moveTo(sx((c[0][0] + c[1][0]) / 2), sy((c[0][1] + c[1][1]) / 2)); ctx.lineTo(sx(g[0]), sy(g[1])); ctx.stroke();
      const gr = obbRotateRect(o, s);
      ctx.strokeStyle = "#222222"; ctx.fillStyle = "#4FC3F7"; circle(g, (gr.x2 - gr.x1) / 2); ctx.fill(); ctx.stroke();
    };

    for (const a of this.doc.anns) {
      const color = this.doc.colorOf(a), selected = this.doc.selection.has(a.key);
      if (a.kind === "bbox") drawBox(a.rect!, color, selected);
      else if (a.obb) drawObb(a.obb, color, selected);
      else drawPolygon(a.points!, color, selected);
    }

    // The shape under the pointer in the side panel's list: a light halo, so the row and the shape can be matched.
    const hov = this.doc.hovered === null ? null : this.doc.anns.find((a) => a.key === this.doc.hovered);
    if (hov) {
      const r = hov.rect;
      const pts: Pt[] = hov.kind === "bbox" && r ? [[r.x1, r.y1], [r.x2, r.y1], [r.x2, r.y2], [r.x1, r.y2]] : hov.points ?? [];
      if (pts.length) {
        ctx.beginPath(); pts.forEach(([x, y], i) => (i ? ctx.lineTo(sx(x), sy(y)) : ctx.moveTo(sx(x), sy(y)))); ctx.closePath();
        ctx.setLineDash([]);
        ctx.lineWidth = lineW + 6; ctx.strokeStyle = "rgba(255,255,255,0.55)"; ctx.stroke();
        ctx.lineWidth = lineW + 1; ctx.strokeStyle = this.doc.colorOf(hov); ctx.stroke();
      }
    }

    const toolColor = this.doc.currentClass?.color_hex ?? POLY_TOOL_COLOR;
    const dr = this.drag;
    if (dr?.kind === "box") {
      const r = normalized({ x1: dr.start[0], y1: dr.start[1], x2: dr.cur[0], y2: dr.cur[1] });
      drawBox(r, this.doc.currentClass?.color_hex ?? "#4FC3F7", false);
    } else if (dr?.kind === "obox") {                         // OBBTool's dashed preview rectangle
      const r = normalized({ x1: dr.start[0], y1: dr.start[1], x2: dr.cur[0], y2: dr.cur[1] });
      ctx.strokeStyle = toolColor; ctx.lineWidth = 2; ctx.setLineDash([8, 4]);
      ctx.strokeRect(sx(r.x1), sy(r.y1), (r.x2 - r.x1) * s, (r.y2 - r.y1) * s);
      ctx.setLineDash([]);
    } else if (dr?.kind === "band") {
      const r = normalized({ x1: dr.start[0], y1: dr.start[1], x2: dr.cur[0], y2: dr.cur[1] });
      ctx.fillStyle = "rgba(46,134,245,0.15)"; ctx.strokeStyle = "rgba(46,134,245,0.9)"; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
      ctx.fillRect(sx(r.x1), sy(r.y1), (r.x2 - r.x1) * s, (r.y2 - r.y1) * s);
      ctx.strokeRect(sx(r.x1), sy(r.y1), (r.x2 - r.x1) * s, (r.y2 - r.y1) * s);
      ctx.setLineDash([]);
    }

    if (this.poly.length) {                                    // the polygon being traced: edges, dots, and a dashed line to the cursor
      const line = (a: Pt, b: Pt, dashed: boolean) => {
        ctx.strokeStyle = toolColor; ctx.lineWidth = 2; ctx.setLineDash(dashed ? [8, 4] : []);
        ctx.beginPath(); ctx.moveTo(sx(a[0]), sy(a[1])); ctx.lineTo(sx(b[0]), sy(b[1])); ctx.stroke();
      };
      for (let i = 1; i < this.poly.length; i++) line(this.poly[i - 1], this.poly[i], false);
      if (this.polyCursor) line(this.poly[this.poly.length - 1], this.polyCursor, true);
      ctx.setLineDash([]); ctx.fillStyle = toolColor;
      for (const [x, y] of this.poly) { ctx.beginPath(); ctx.arc(sx(x), sy(y), VERTEX_RADIUS, 0, Math.PI * 2); ctx.fill(); }
    }
  }

  /**
   * Qt's highlight for a selected item that does not suppress it (the rotated box): a dashed rectangle in the text
   * colour over a solid one in the contrasting colour, round the item's bounding rect (corners plus the handle and
   * grip margin, which is in screen pixels) pulled in by half the 2-unit pen.
   */
  private selectionOutline(ctx: CanvasRenderingContext2D, corners: Pt[], s: number, sx: (x: number) => number, sy: (y: number) => number) {
    const b = polygonBounds(corners);
    const m = sceneLength(9, s) + sceneLength(ROTATE_HANDLE_GAP + 6, s) - 1;
    const x = sx(b.x1 - m) + 0.5, y = sy(b.y1 - m) + 0.5, w = (b.x2 - b.x1 + 2 * m) * s, h = (b.y2 - b.y1 + 2 * m) * s;
    const [r, g, bl] = (getComputedStyle(this.root).color.match(/\d+/g) ?? ["230", "232", "240"]).map(Number);
    const fg = `rgb(${r},${g},${bl})`, bg = r > 127 ? "rgb(28,29,30)" : "rgb(255,255,255)";
    ctx.lineWidth = 1;
    ctx.setLineDash([]); ctx.strokeStyle = bg; ctx.strokeRect(x, y, w, h);
    ctx.setLineDash([2, 4]); ctx.strokeStyle = fg; ctx.strokeRect(x, y, w, h);
    ctx.setLineDash([]);
  }
}

function hexA(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  return m ? `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${alpha})` : hex;
}
